import React from 'react'
import { useSelector, useDispatch } from 'react-redux'

import './style.css'

import { actions as NotificationsActions } from 'data/notifications'
import { getChannel } from 'data/channels/selectors'
import { getNote } from 'data/notes/selectors'
import { isPersonal } from 'data/channels/record'

import PingInner from './Inner'
import IconButton from 'components/Utils/IconButton'
import { NudgeIcon } from 'components/Utils/Icons'
import PopoverOnClick from 'components/Utils/Popover/PopoverOnClick'
import { measureTimingStart } from 'data/metrics'

const NUDGE_THRESHOLD = 3000

type MetabarPingProps = {
  noteId: string
}

const MetabarPing = React.memo(({ noteId }: MetabarPingProps) => {
  const [nudgedUsers, setNudgedUsers] = React.useState({})
  const timeoutHandles = React.useRef<number[]>([])
  const dispatch = useDispatch()
  const isPersonalChannel = useSelector(state => {
    const note = getNote(state, noteId)
    if (!note) {
      return false
    }
    const channel = getChannel(state, note.channelId)
    return isPersonal(channel)
  })

  React.useEffect(() => {
    return () => {
      // We use a ref, so ref will be valid
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timeoutHandles.current.forEach(window.clearTimeout)
    }
  }, [])

  const nudge = React.useCallback(
    user => {
      dispatch(
        NotificationsActions.nudge({
          noteId,
          userId: user.id,
        })
      )

      setNudgedUsers({
        ...nudgedUsers,
        [user.id]: true,
      })

      const timeout = window.setTimeout(() => {
        setNudgedUsers({
          ...nudgedUsers,
          [user.id]: false,
        })
      }, NUDGE_THRESHOLD)
      timeoutHandles.current.push(timeout)
    },
    [dispatch, nudgedUsers, noteId]
  )

  const getContent = React.useCallback(
    ({ closePopover }) => {
      return (
        <PingInner
          closePopover={closePopover}
          noteId={noteId}
          nudgedUsers={nudgedUsers}
          nudge={nudge}
        />
      )
    },
    [noteId, nudge, nudgedUsers]
  )

  const onMouseUp = React.useCallback(() => {
    measureTimingStart('TIME_TO_OPEN_PING')
  }, [])

  if (isPersonalChannel) {
    return null
  }

  return (
    <PopoverOnClick position="left" align="start" content={getContent}>
      <IconButton
        style={{ marginBottom: 2 }}
        tooltipPosition="left"
        tooltip="Ping members to this doc"
        onMouseUp={onMouseUp}
      >
        <NudgeIcon size={20} />
      </IconButton>
    </PopoverOnClick>
  )
})

export default MetabarPing
